import { useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { GameRegistryContext } from './GameRegistryContextTypes';
import type { Game, ChallengeWithProgress, ChallengeContextState } from '@/types/game';
import { apiClient, type ApiError } from '@/services/api-client';

export function GameRegistryProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Challenge state management
  const [challengeState, setChallengeState] = useState<ChallengeContextState>({
    challengesByGame: {},
    loading: {},
    error: {},
    lastFetch: {},
    permanentlyFailed: {},
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
  });

  // Refs for timers and request-generation counters
  const refreshTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const gamesRequestIdRef = useRef(0);
  const challengeRequestIdsRef = useRef<Record<string, number>>({});

  const fetchGames = useCallback(async () => {
    const myRequestId = ++gamesRequestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getGames();
      if (myRequestId !== gamesRequestIdRef.current) return;
      setGames(response.games);
    } catch (err) {
      if (myRequestId !== gamesRequestIdRef.current) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch games';
      setError(new Error(errorMessage));
      console.error('Failed to fetch games:', err);
    } finally {
      if (myRequestId === gamesRequestIdRef.current) setLoading(false);
    }
  }, []);

  const refreshGames = useCallback(async () => {
    await fetchGames();
  }, [fetchGames]);

  const getGameById = useCallback((gameId: string): Game | undefined => {
    return games.find(game => game.id === gameId);
  }, [games]);

  // Challenge methods
  const getChallengesForGame = useCallback(async (gameId: string): Promise<ChallengeWithProgress[]> => {

    const now = Date.now();
    const lastFetch = challengeState.lastFetch[gameId] || 0;
    const isExpired = now - lastFetch > challengeState.cacheTimeout;

    // Don't retry games that permanently failed with validation errors
    if (challengeState.permanentlyFailed[gameId]) {
      console.warn(`Skipping fetch for permanently failed game: ${gameId}`);
      return challengeState.challengesByGame[gameId] || [];
    }

    // Return cached data if available and not expired
    if (!isExpired && challengeState.challengesByGame[gameId]) {
      return challengeState.challengesByGame[gameId];
    }

    // Don't fetch if already loading this game
    if (challengeState.loading[gameId]) {
      // Return cached data or empty array while loading
      return challengeState.challengesByGame[gameId] || [];
    }

    const myRequestId = (challengeRequestIdsRef.current[gameId] ?? 0) + 1;
    challengeRequestIdsRef.current[gameId] = myRequestId;

    setChallengeState(prev => ({
      ...prev,
      loading: { ...prev.loading, [gameId]: true },
      error: { ...prev.error, [gameId]: null },
    }));

    try {
      const guestToken = localStorage.getItem('hub_guest_token') ?? undefined;
      const challenges = await apiClient.getProgress(gameId, guestToken);
      if (myRequestId !== challengeRequestIdsRef.current[gameId]) return [];
      setChallengeState(prev => ({
        ...prev,
        challengesByGame: { ...prev.challengesByGame, [gameId]: challenges },
        loading: { ...prev.loading, [gameId]: false },
        lastFetch: { ...prev.lastFetch, [gameId]: now },
      }));
      return challenges;
    } catch (err) {
      if (myRequestId !== challengeRequestIdsRef.current[gameId]) return [];
      const apiError = err as ApiError;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch challenges';
      const error = new Error(errorMessage);

      // Check if this is a validation error that should not be retried
      const isValidationError = apiError.status === 400 ||
                                (apiError.error === 'INVALID_GAME_ID' ||
                                 errorMessage.includes('Invalid game ID format'));

      if (isValidationError) {
        console.error(`Game ${gameId} has invalid format and will not be retried:`, errorMessage);

        setChallengeState(prev => ({
          ...prev,
          loading: { ...prev.loading, [gameId]: false },
          error: { ...prev.error, [gameId]: error },
          permanentlyFailed: { ...prev.permanentlyFailed, [gameId]: true },
        }));

        // Return empty array instead of throwing to prevent infinite loops
        return [];
      }

      // For other errors, update state and re-throw for retry logic
      setChallengeState(prev => ({
        ...prev,
        loading: { ...prev.loading, [gameId]: false },
        error: { ...prev.error, [gameId]: error },
      }));
      throw error;
    }
  }, [challengeState, games]);

  const refreshChallenges = useCallback(async (gameId: string): Promise<void> => {
    // Clear cache and permanent failure status for this game (allow manual retry)
    setChallengeState(prev => ({
      ...prev,
      lastFetch: { ...prev.lastFetch, [gameId]: 0 },
      permanentlyFailed: { ...prev.permanentlyFailed, [gameId]: false },
    }));
    await getChallengesForGame(gameId);
  }, [getChallengesForGame]);

  const invalidateCache = useCallback((gameId?: string) => {
    if (gameId) {
      // Invalidate cache for specific game
      setChallengeState(prev => ({
        ...prev,
        lastFetch: { ...prev.lastFetch, [gameId]: 0 },
      }));
    } else {
      // Invalidate all caches
      setChallengeState(prev => ({
        ...prev,
        lastFetch: {},
      }));
    }
  }, []);

  const scheduleNearMidnightRefresh = useCallback((gameId: string) => {
    // Clear existing timer for this game
    if (refreshTimers.current[gameId]) {
      clearTimeout(refreshTimers.current[gameId]);
    }

    // Calculate time until next midnight local time (approximately)
    const now = new Date();
    const nextMidnight = new Date();
    nextMidnight.setHours(24, 0, 0, 0); // Next midnight local time
    
    const timeUntilMidnight = nextMidnight.getTime() - now.getTime();
    const timeUntilRefresh = Math.max(timeUntilMidnight - (10 * 60 * 1000), 60000); // 10 minutes before midnight, but at least 1 minute

    refreshTimers.current[gameId] = setTimeout(() => {
      refreshChallenges(gameId);
    }, timeUntilRefresh);
  }, [refreshChallenges]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(refreshTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Fetch games once Clerk is loaded, and refetch whenever sign-in state changes.
  // This prevents stale unauthenticated displayState from showing locked challenges
  // after sign-in (or locked→unlocked flash on initial load).
  useEffect(() => {
    if (isLoaded) {
      fetchGames();
    }
  }, [isLoaded, isSignedIn, fetchGames]);

  const value = {
    games,
    loading,
    error,
    fetchGames,
    refreshGames,
    getGameById,
    challengeState,
    getChallengesForGame,
    refreshChallenges,
    invalidateCache,
    scheduleNearMidnightRefresh,
  };

  return (
    <GameRegistryContext.Provider value={value}>
      {children}
    </GameRegistryContext.Provider>
  );
}